import type { SedrFile } from '../api/sedr';
import type { SharedFolderManifestPlaintext } from '../crypto/sharedFolderManifest';
import { parentManifestPath } from '../crypto/sharedFolderManifest';
import { applyI18n } from '../i18n';

export interface FilesTreeSearchEntry {
  /** Parent folder key (`''` = root). */
  parentKey: string;
  /** Stable id for the entry (SEDR file id or Fileshare path). */
  entryKey: string;
  name: string;
  kind: 'file' | 'directory';
}

export type FilesTreeSearchMatch = FilesTreeSearchEntry;

export interface FilesTreeSearchState {
  active: boolean;
  /** Trimmed query used for highlighting. */
  query: string;
  hitCounts: Map<string, number>;
  /** Folder entry keys whose own name matched — these must not be dimmed. */
  nameMatchedKeys: Set<string>;
}

const nameCmp = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

function pathBasename(path: string): string {
  const slashIndex = path.lastIndexOf('/');
  return slashIndex === -1 ? path : path.slice(slashIndex + 1);
}

function dedupeAndSort(list: FilesTreeSearchEntry[]): FilesTreeSearchEntry[] {
  const seen = new Set<string>();
  const deduped: FilesTreeSearchEntry[] = [];
  for (const item of list) {
    if (seen.has(item.entryKey)) continue;
    seen.add(item.entryKey);
    deduped.push(item);
  }
  deduped.sort((a, b) => nameCmp(a.name, b.name));
  return deduped;
}

/** Collect name matches from a flat entry list already in DFS sibling order. */
export function collectNameMatches(
  query: string,
  entries: readonly FilesTreeSearchEntry[],
): FilesTreeSearchMatch[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  return entries.filter((entry) => entry.name.toLowerCase().includes(needle));
}

/** Direct hit count per parent folder key. */
export function directHitCounts(matches: readonly FilesTreeSearchMatch[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const match of matches) {
    counts.set(match.parentKey, (counts.get(match.parentKey) ?? 0) + 1);
  }
  return counts;
}

export function stepSearchIndex(current: number, direction: -1 | 1, length: number): number {
  if (length <= 0) return -1;
  if (current < 0) return direction > 0 ? 0 : length - 1;
  return (current + direction + length) % length;
}

export function toSearchState(query: string, matches: readonly FilesTreeSearchMatch[]): FilesTreeSearchState {
  const trimmed = query.trim();
  const active = trimmed.length > 0;
  const nameMatchedKeys = new Set<string>();
  if (active) {
    for (const match of matches) {
      if (match.kind === 'directory') nameMatchedKeys.add(match.entryKey);
    }
  }
  return {
    active,
    query: active ? trimmed : '',
    hitCounts: active ? directHitCounts(matches) : new Map(),
    nameMatchedKeys,
  };
}

/** Fill `host` with `name`, wrapping case-insensitive query matches in `.files-search-highlight`. */
export function fillHighlightedText(host: HTMLElement, name: string, query: string): void {
  host.replaceChildren();
  const needle = query.trim();
  if (!needle) {
    host.textContent = name;
    return;
  }

  const lowerName = name.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  let cursor = 0;
  let found = false;

  while (cursor < name.length) {
    const index = lowerName.indexOf(lowerNeedle, cursor);
    if (index < 0) {
      host.append(document.createTextNode(name.slice(cursor)));
      break;
    }
    found = true;
    if (index > cursor) {
      host.append(document.createTextNode(name.slice(cursor, index)));
    }
    const mark = document.createElement('mark');
    mark.className = 'files-search-highlight';
    mark.textContent = name.slice(index, index + needle.length);
    host.append(mark);
    cursor = index + needle.length;
  }

  if (!found) {
    host.textContent = name;
  }
}

/** Walk SEDR files in DFS order (children sorted by name). */
export function sedrEntriesInDfsOrder(files: readonly SedrFile[]): FilesTreeSearchEntry[] {
  const byParent = new Map<string, SedrFile[]>();
  for (const file of files) {
    const parentKey = file.parentId ?? '';
    const list = byParent.get(parentKey);
    if (list) list.push(file);
    else byParent.set(parentKey, [file]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => nameCmp(a.name, b.name));
  }

  const out: FilesTreeSearchEntry[] = [];
  const visit = (parentKey: string) => {
    for (const child of byParent.get(parentKey) ?? []) {
      out.push({
        parentKey,
        entryKey: child.id,
        name: child.name,
        kind: child.isDirectory ? 'directory' : 'file',
      });
      if (child.isDirectory) visit(child.id);
    }
  };
  visit('');
  return out;
}

export function collectSedrNameMatches(query: string, files: readonly SedrFile[]): FilesTreeSearchMatch[] {
  return collectNameMatches(query, sedrEntriesInDfsOrder(files));
}

/** Walk Fileshare manifest entries in DFS order. */
export function manifestEntriesInDfsOrder(
  manifest: SharedFolderManifestPlaintext,
): FilesTreeSearchEntry[] {
  const byParent = new Map<string, FilesTreeSearchEntry[]>();

  const push = (entry: FilesTreeSearchEntry) => {
    const list = byParent.get(entry.parentKey);
    if (list) list.push(entry);
    else byParent.set(entry.parentKey, [entry]);
  };

  const ensureDir = (dirPath: string) => {
    if (!dirPath) return;
    const parentKey = parentManifestPath(dirPath);
    const existing = (byParent.get(parentKey) ?? []).some(
      (e) => e.entryKey === dirPath && e.kind === 'directory',
    );
    if (existing) return;
    ensureDir(parentKey);
    push({
      parentKey,
      entryKey: dirPath,
      name: pathBasename(dirPath),
      kind: 'directory',
    });
  };

  for (const entry of manifest.entries) {
    const parentKey = parentManifestPath(entry.path);
    ensureDir(parentKey);
    push({
      parentKey,
      entryKey: entry.path,
      name: pathBasename(entry.path),
      kind: entry.type === 'directory' ? 'directory' : 'file',
    });
  }

  for (const [key, list] of byParent) {
    byParent.set(key, dedupeAndSort(list));
  }

  const out: FilesTreeSearchEntry[] = [];
  const visit = (parentKey: string) => {
    for (const child of byParent.get(parentKey) ?? []) {
      out.push(child);
      if (child.kind === 'directory') visit(child.entryKey);
    }
  };
  visit('');
  return out;
}

export function collectManifestNameMatches(
  query: string,
  manifest: SharedFolderManifestPlaintext,
): FilesTreeSearchMatch[] {
  return collectNameMatches(query, manifestEntriesInDfsOrder(manifest));
}

/**
 * Expand folders so every match's parent folder node is visible.
 * `expand` receives folder keys that should be marked expanded.
 */
export function expandFoldersForSearchHits(
  matches: readonly FilesTreeSearchMatch[],
  expand: (folderKey: string) => void,
  ancestorsOf: (folderKey: string) => readonly string[],
): void {
  for (const match of matches) {
    for (const ancestor of ancestorsOf(match.parentKey)) {
      expand(ancestor);
    }
  }
}

/** Path-style ancestors of a folder key (includes root `''`, excludes the key itself). */
export function pathAncestorKeys(folderKey: string): string[] {
  if (!folderKey) return [];
  const out: string[] = [''];
  const parts = folderKey.split('/');
  let built = '';
  for (let i = 0; i < parts.length - 1; i++) {
    built = built ? `${built}/${parts[i]}` : parts[i]!;
    out.push(built);
  }
  return out;
}

/** SEDR ancestors of a folder id (includes root `''`, excludes the folder itself). */
export function sedrAncestorKeys(files: readonly SedrFile[], folderId: string | null): string[] {
  if (!folderId) return [];
  const byId = new Map<string, SedrFile>();
  for (const f of files) {
    if (f.isDirectory) byId.set(f.id, f);
  }
  const parents: string[] = [];
  let cur: string | null = folderId;
  while (cur) {
    const dir = byId.get(cur);
    if (!dir) break;
    cur = dir.parentId ?? null;
    parents.push(cur ?? '');
  }
  return parents.reverse();
}

export function applyDirTreeNodeContent(
  button: HTMLElement,
  options: {
    icon: HTMLElement;
    name: string;
    search?: FilesTreeSearchState | null;
    /** Hit count for this folder key (matches whose parentKey === this node). */
    folderKey: string;
  },
): void {
  const search = options.search;
  const searchActive = Boolean(search?.active);
  const hitCount = searchActive ? (search!.hitCounts.get(options.folderKey) ?? 0) : 0;
  const selfMatched = searchActive ? search!.nameMatchedKeys.has(options.folderKey) : false;

  button.replaceChildren();
  const nameSpan = document.createElement('span');
  nameSpan.className = 'files-dir-tree__name';
  fillHighlightedText(nameSpan, options.name, searchActive ? search!.query : '');
  button.append(options.icon, nameSpan);

  if (searchActive && hitCount > 0) {
    const badge = document.createElement('span');
    badge.className = 'files-dir-tree__hit-count';
    badge.textContent = `(${hitCount})`;
    badge.setAttribute('aria-hidden', 'true');
    button.append(badge);
  }

  button.classList.toggle(
    'files-dir-tree__node--no-hits',
    searchActive && hitCount === 0 && !selfMatched,
  );
}

export interface FilesTreeSearchBarHandle {
  setQuery(query: string): void;
  getQuery(): string;
  setResultCount(count: number): void;
  focus(): void;
}

export function mountFilesTreeSearchBar(
  host: HTMLElement,
  options: {
    placeholderKey?: string;
    prevLabelKey?: string;
    nextLabelKey?: string;
    initialQuery?: string;
    onQueryChange: (query: string) => void;
    onStep: (direction: -1 | 1) => void;
  },
): FilesTreeSearchBarHandle {
  host.className = 'files-tree-search';
  host.replaceChildren();

  const input = document.createElement('input');
  input.type = 'search';
  input.className = 'files-tree-search__input';
  input.dataset.i18nPlaceholder = options.placeholderKey ?? 'files.search.placeholder';
  if (options.initialQuery) input.value = options.initialQuery;

  const nav = document.createElement('div');
  nav.className = 'files-tree-search__nav';

  const prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.className = 'files-tree-search__nav-btn';
  prevBtn.dataset.i18nAriaLabel = options.prevLabelKey ?? 'files.search.prev';
  prevBtn.textContent = '‹';
  prevBtn.disabled = true;

  const nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.className = 'files-tree-search__nav-btn';
  nextBtn.dataset.i18nAriaLabel = options.nextLabelKey ?? 'files.search.next';
  nextBtn.textContent = '›';
  nextBtn.disabled = true;

  nav.append(prevBtn, nextBtn);
  host.append(input, nav);
  applyI18n(host);

  let resultCount = 0;

  const syncNav = () => {
    const enabled = resultCount > 0;
    prevBtn.disabled = !enabled;
    nextBtn.disabled = !enabled;
  };

  input.addEventListener('input', () => {
    options.onQueryChange(input.value);
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      options.onStep(event.shiftKey ? -1 : 1);
    }
  });

  prevBtn.addEventListener('click', () => options.onStep(-1));
  nextBtn.addEventListener('click', () => options.onStep(1));

  return {
    setQuery(query: string) {
      if (input.value !== query) input.value = query;
    },
    getQuery() {
      return input.value;
    },
    setResultCount(count: number) {
      resultCount = count;
      syncNav();
    },
    focus() {
      input.focus();
    },
  };
}
